import { Button, Flex, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { reactEvents } from '@bridge/reactEvents';
import { Supplier, suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useNavigationStore } from '@texas/components/navigation/hooks/useNavigationStore';
import {
  isStateActive,
  NavigationRouteItem,
} from '@texas/components/navigation/context/navigationItems';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { PageBreadcrumb } from '@texas/components/shared/PageBreadcrumb';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import {
  entityWorkspaceStyles,
  getEntityWorkspaceNavigationItemStyle,
} from '@texas/resources/theme/styles/entityWorkspace';
import { getInitials } from '@texas/utils/helpers/commonHelpers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateSupplier } from './UpdateSupplier';

interface Props {
  supplierId: number;
}

interface NavigationItem extends NavigationRouteItem {
  allow?: Role[];
}

const navigationItems: NavigationItem[] = [
  { label: 'navigation.overview', route: 'supplier.management' },
  { label: 'general.contacts', route: 'supplier.contacts' },
  { label: 'suppliers.productGroups', route: 'supplier.productGroups' },
  {
    label: 'general.articles',
    route: 'supplier.articles',
    allow: [Role.SystemAdministrator],
  },
  {
    label: 'complianceDocumentDefinitions.title',
    route: 'supplier.complianceDocuments',
  },
  { label: 'navigation.files', route: 'supplier.files.id' },
];

export function SupplierWorkspaceHeader({ supplierId }: Props) {
  const { t } = useTranslation();
  const translateLabel = (label: string) => t(label as 'navigation.overview');
  const { activeState } = useNavigationStore();
  const {
    data: supplier,
    refetch,
    set,
    loading,
    error,
  } = useApiResource(suppliersApi.get);
  const editDisclosure = useDisclosure();
  const archiveDisclosure = useDisclosure();

  useEffect(() => {
    refetch(supplierId);
  }, [refetch, supplierId]);

  if (loading || !supplier) {
    return (
      <BodyOverride>
        <Flex sx={entityWorkspaceStyles.loading}>
          {error ? <ErrorDetails error={error} /> : <Spinner />}
        </Flex>
      </BodyOverride>
    );
  }

  const updateSupplier = (updatedSupplier: Supplier) => {
    set(updatedSupplier);
    reactEvents.supplierNameChanged.dispatch({
      id: updatedSupplier.id,
      value: updatedSupplier.name,
    });
  };

  return (
    <BodyOverride>
      <PageBreadcrumb
        items={[
          { label: t('general.suppliers'), route: 'suppliers' },
          { label: supplier.name },
        ]}
        label={t('suppliers.workspace.breadcrumb')}
      />
      <Flex as="header" sx={entityWorkspaceStyles.header}>
        <Flex sx={entityWorkspaceStyles.headerContent}>
          <Flex sx={entityWorkspaceStyles.identity}>
            <Flex sx={entityWorkspaceStyles.initials}>
              {getInitials(supplier.name)}
            </Flex>
            <Flex sx={entityWorkspaceStyles.identityText}>
              <Flex sx={entityWorkspaceStyles.titleRow}>
                <Text sx={entityWorkspaceStyles.title}>{supplier.name}</Text>
                {supplier.archived && (
                  <Text sx={entityWorkspaceStyles.metadata}>
                    {t('general.archived')}
                  </Text>
                )}
              </Flex>
              <Text sx={entityWorkspaceStyles.metadata}>
                {t('suppliers.workspace.profile')} ·{' '}
                {supplier.isInternal
                  ? t('suppliers.internalSupplier')
                  : t('suppliers.externalSupplier')}
              </Text>
            </Flex>
          </Flex>
          <RoleRestrict
            allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
          >
            <Flex sx={entityWorkspaceStyles.actions}>
              <Button
                variant="entityDirectorySecondary"
                onClick={archiveDisclosure.onOpen}
              >
                {supplier.archived
                  ? t('general.restore')
                  : t('general.archive')}
              </Button>
              <Button
                variant="entityDirectoryPrimary"
                onClick={editDisclosure.onOpen}
              >
                {t('suppliers.workspace.editSupplier')}
              </Button>
            </Flex>
          </RoleRestrict>
        </Flex>
        <Flex
          as="nav"
          aria-label={t('suppliers.workspace.navigation')}
          sx={entityWorkspaceStyles.navigation}
        >
          {navigationItems.map((item) => {
            const active = isStateActive(activeState, item.route);
            const link = (
              <AngularNavLink
                key={item.route}
                to={item.route}
                navobj={{ supplierId }}
                aria-current={active ? 'page' : undefined}
                sx={getEntityWorkspaceNavigationItemStyle(active)}
              >
                {translateLabel(item.label)}
              </AngularNavLink>
            );

            return item.allow ? (
              <RoleRestrict key={item.route} allow={item.allow}>
                {link}
              </RoleRestrict>
            ) : (
              link
            );
          })}
        </Flex>
      </Flex>
      <UpdateSupplier
        supplier={supplier}
        isOpen={editDisclosure.isOpen}
        onClose={editDisclosure.onClose}
        onUpdated={updateSupplier}
      />
      {archiveDisclosure.isOpen && (
        <VerifyDialogWithRequest
          headerTitle={
            supplier.archived
              ? t('suppliers.workspace.restoreSupplier', {
                  name: supplier.name,
                })
              : t('suppliers.workspace.archiveSupplier', {
                  name: supplier.name,
                })
          }
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={
            supplier.archived ? suppliersApi.restore : suppliersApi.archive
          }
          args={[supplier.id]}
          isOpen={archiveDisclosure.isOpen}
          onClose={archiveDisclosure.onClose}
          onPerformed={(updatedSupplier) => {
            set(updatedSupplier);
            if (updatedSupplier.archived) {
              reactEvents.supplierArchived.dispatch(updatedSupplier.id);
            } else {
              reactEvents.supplierRestored.dispatch(updatedSupplier.id);
            }
          }}
          onSuccessTitle={
            supplier.archived
              ? t('general.entityRestored', { name: supplier.name })
              : t('general.entityArchived', { name: supplier.name })
          }
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
      )}
    </BodyOverride>
  );
}
